import { PendingCalendarItem } from '../../../contracts/FMPendingContracts';

/** Month labels (index 0 = "ALL MONTHS"), parity of `pendingevent-actions.controller.js` monthNames. */
export const MONTH_NAMES = [
  'ALL MONTHS',
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
];

const lc = (s?: string): string => (s || '').toLowerCase();

/** Group pending items by resource alias (parity: `_.groupBy(items, ResourceId.toLowerCase())`). */
export function groupByResource(
  items: PendingCalendarItem[]
): Record<string, PendingCalendarItem[]> {
  const out: Record<string, PendingCalendarItem[]> = {};
  for (const it of items) {
    const key = lc(it.ResourceId);
    if (!out[key]) {
      out[key] = [];
    }
    out[key].push(it);
  }
  return out;
}

/** Group pending items by delegating manager alias (parity: delegated grouping). */
export function groupByManager(
  items: PendingCalendarItem[]
): Record<string, PendingCalendarItem[]> {
  const out: Record<string, PendingCalendarItem[]> = {};
  for (const it of items) {
    const key = lc(it.ManagerAlias);
    if (!out[key]) {
      out[key] = [];
    }
    out[key].push(it);
  }
  return out;
}

/** Filter by selected event-type names (lowercased). Empty selection = all (parity: FilterEventsData). */
export function filterByEventTypes(
  items: PendingCalendarItem[],
  selectedLower: string[]
): PendingCalendarItem[] {
  if (!selectedLower || selectedLower.length === 0) {
    return items;
  }
  return items.filter((i) => selectedLower.indexOf(lc(i.EventTypeName)) !== -1);
}

/** Distinct years present across items' start dates, ascending. */
export function getYears(items: PendingCalendarItem[]): number[] {
  const years = new Set<number>();
  for (const it of items) {
    const d = new Date(it.StartDate);
    if (!isNaN(d.getTime())) {
      years.add(d.getFullYear());
    }
  }
  return Array.from(years).sort((a, b) => a - b);
}

export interface MonthCount {
  month: number; // 1-12
  monthName: string;
  count: number;
}

/** Per-month counts for a year (optionally event-type filtered) — the month chips. */
export function countByMonthForYear(
  items: PendingCalendarItem[],
  year: number,
  selectedLower: string[] = []
): MonthCount[] {
  const filtered = filterByEventTypes(items, selectedLower);
  const counts: Record<number, number> = {};
  for (const it of filtered) {
    const d = new Date(it.StartDate);
    if (isNaN(d.getTime()) || d.getFullYear() !== year) {
      continue;
    }
    const m = d.getMonth() + 1;
    counts[m] = (counts[m] || 0) + 1;
  }
  return Object.keys(counts)
    .map((k) => parseInt(k, 10))
    .sort((a, b) => a - b)
    .map((m) => ({ month: m, monthName: MONTH_NAMES[m], count: counts[m] }));
}

/** Total count (optionally event-type filtered) — parity of managerApprovalCount. */
export function totalCount(
  items: PendingCalendarItem[],
  selectedLower: string[] = []
): number {
  return filterByEventTypes(items, selectedLower).length;
}
